/**
 * Instruments Panel - Combines Virtual Keyboard and Drum Pads
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Piano, Drum, X, ChevronDown, ChevronUp } from 'lucide-react';
import { VirtualKeyboard } from './VirtualKeyboard';
import { DrumPads } from './DrumPads';

export const InstrumentsPanel = ({
  isOpen,
  onClose,
  onNoteOn,
  onNoteOff,
  onDrumHit,
  activeNotes = [],
  activePads = [],
  isRecording = false,
  selectedInstrument = 'keyboard', // 'keyboard' or 'drums'
  onInstrumentChange,
}) => {
  const [octave, setOctave] = useState(4);
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 300, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 300, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-40 bg-zinc-950/95 backdrop-blur-xl border-t border-zinc-800"
        data-testid="instruments-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800">
          <div className="flex items-center gap-4">
            <span className="text-sm font-semibold">Virtual Instruments</span>
            
            {/* Instrument Selector */}
            <div className="flex items-center gap-1 bg-zinc-800/50 rounded-lg p-1">
              <Button
                variant={selectedInstrument === 'keyboard' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onInstrumentChange?.('keyboard')}
                className={`h-7 px-3 ${selectedInstrument === 'keyboard' ? 'bg-indigo-500' : ''}`}
                data-testid="select-keyboard"
              >
                <Piano className="w-4 h-4 mr-1" />
                Keys
              </Button>
              <Button
                variant={selectedInstrument === 'drums' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onInstrumentChange?.('drums')}
                className={`h-7 px-3 ${selectedInstrument === 'drums' ? 'bg-amber-500' : ''}`}
                data-testid="select-drums"
              >
                <Drum className="w-4 h-4 mr-1" />
                Drums
              </Button>
            </div>
            
            {isRecording && (
              <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full animate-pulse flex items-center gap-1">
                <span className="w-2 h-2 bg-red-500 rounded-full" />
                Recording
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="text-zinc-400 hover:text-white"
              data-testid="collapse-instruments"
            >
              {isCollapsed ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-zinc-400 hover:text-white"
              data-testid="close-instruments"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <AnimatePresence>
          {!isCollapsed && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 max-w-4xl mx-auto">
                {selectedInstrument === 'keyboard' ? (
                  <VirtualKeyboard
                    onNoteOn={onNoteOn}
                    onNoteOff={onNoteOff}
                    activeNotes={activeNotes}
                    octave={octave}
                    onOctaveChange={setOctave}
                    isRecording={isRecording}
                    keyboardEnabled={true}
                  />
                ) : (
                  <DrumPads
                    onPadHit={onDrumHit}
                    activePads={activePads}
                    isRecording={isRecording}
                  />
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstrumentsPanel;
